import { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Calendar, Users, Database, AlertCircle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRelative = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

export const PostingsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/loads/stats/postings`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load posting stats');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStats(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 px-6 py-8 text-center">
        <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-3" />
        <p className="text-red-900 font-bold mb-1">Couldn&apos;t load posting stats</p>
        <p className="text-red-700 text-sm">{error}</p>
      </div>
    );
  }

  const agents = data?.agents || [];
  const totals = agents.reduce(
    (acc, a) => ({
      today: acc.today + (a.today || 0),
      week:  acc.week  + (a.week  || 0),
      month: acc.month + (a.month || 0),
      year:  acc.year  + (a.year  || 0),
      all:   acc.all   + (a.all_time || 0),
    }),
    { today: 0, week: 0, month: 0, year: 0, all: 0 }
  );

  return (
    <div className="space-y-5" data-testid="postings-tab">
      {/* Header strip */}
      <div className="bg-zinc-900 text-white px-5 py-4">
        <div className="flex items-center gap-3 mb-1">
          <TrendingUp className="w-5 h-5 text-red-500" />
          <h2 className="text-lg font-black uppercase tracking-wide">Postings Analytics</h2>
        </div>
        <p className="text-zinc-400 text-xs leading-relaxed max-w-2xl">
          Immutable count of every load posted to the board (Local + TMS).
          Records survive load expiry &amp; deletion — use this to track dispatcher productivity over time.
          All windows in <strong className="text-zinc-200">{data?.window?.tz || 'US/Mountain'}</strong>.
        </p>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatBox label="Today"      value={totals.today} />
        <StatBox label="This Week"  value={totals.week}  />
        <StatBox label="This Month" value={totals.month} />
        <StatBox label="This Year"  value={totals.year}  />
        <StatBox label="All Time"   value={totals.all}   accent />
      </div>

      {/* Per-agent table */}
      <div className="bg-white border border-zinc-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-zinc-500" />
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-700">
              By Agent ({agents.length})
            </span>
          </div>
          <button
            onClick={fetchStats}
            className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 hover:text-red-600 flex items-center gap-1"
            data-testid="postings-refresh"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
        </div>

        {agents.length === 0 ? (
          <div className="py-12 text-center">
            <Calendar className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-zinc-700 mb-1">No posting events yet</p>
            <p className="text-xs text-zinc-500">Posts will appear here as dispatchers add loads.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="postings-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-zinc-500">Agent</th>
                  <th className="px-3 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-zinc-500">Today</th>
                  <th className="px-3 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-zinc-500">Week</th>
                  <th className="px-3 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-zinc-500">Month</th>
                  <th className="px-3 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-zinc-500">Year</th>
                  <th className="px-3 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-zinc-500">All Time</th>
                  <th className="px-3 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-zinc-500">Source</th>
                  <th className="px-3 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-zinc-500">Last Post</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {agents.map((a) => (
                  <tr key={a.agent_email} className="hover:bg-zinc-50" data-testid={`postings-row-${a.agent_email}`}>
                    <td className="px-4 py-3">
                      <div className="text-sm font-bold text-zinc-900">{a.agent_name || '—'}</div>
                      <div className="text-xs text-zinc-500 truncate max-w-[260px]">{a.agent_email || '—'}</div>
                    </td>
                    <td className="px-3 py-3 text-right text-sm font-mono tabular-nums">{a.today}</td>
                    <td className="px-3 py-3 text-right text-sm font-mono tabular-nums">{a.week}</td>
                    <td className="px-3 py-3 text-right text-sm font-mono tabular-nums">{a.month}</td>
                    <td className="px-3 py-3 text-right text-sm font-mono tabular-nums">{a.year}</td>
                    <td className="px-3 py-3 text-right text-sm font-mono tabular-nums font-bold text-red-600">{a.all_time}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider">
                        {a.local > 0 && (
                          <span className="bg-zinc-100 text-zinc-700 px-1.5 py-0.5">L:{a.local}</span>
                        )}
                        {a.tms > 0 && (
                          <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 inline-flex items-center gap-0.5">
                            <Database className="w-2.5 h-2.5" />{a.tms}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right text-xs text-zinc-500 whitespace-nowrap">
                      {formatRelative(a.last_posted_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-[11px] text-zinc-500 leading-relaxed bg-zinc-50 border border-zinc-200 px-4 py-3">
        <strong>Note:</strong> Loads are auto-expired 2 days after their delivery date (hidden from public board)
        and permanently deleted 30 days after that. <em>Posting events here are never deleted</em> — so totals
        reflect the true history of what was posted, not what&apos;s currently visible on the board.
      </div>
    </div>
  );
};

const StatBox = ({ label, value, accent }) => (
  <div className={`px-4 py-3 border ${accent ? 'bg-red-600 border-red-700 text-white' : 'bg-white border-zinc-200 text-zinc-900'}`}>
    <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${accent ? 'text-red-100' : 'text-zinc-500'} mb-1`}>
      {label}
    </div>
    <div className="text-2xl font-black tabular-nums">{value.toLocaleString()}</div>
  </div>
);
